import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { Bookmark, MapPin, Star, MessageSquare, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SavedBartendersPage() {
  const { isBarOwner } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savedBartenders, setSavedBartenders] = useState([]);

  const fetchSavedBartenders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/owners/saved-bartenders');
      setSavedBartenders(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load saved bartenders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isBarOwner) {
      fetchSavedBartenders();
    } else {
      setLoading(false);
    }
  }, [isBarOwner]);

  const unsaveBartender = async (bartenderUserId) => {
    try {
      await axios.delete(`/owners/saved-bartenders/${bartenderUserId}`);
      setSavedBartenders((prev) => prev.filter((item) => item.user_id !== bartenderUserId));
      toast.success('Removed from saved bartenders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove bartender');
    }
  };

  if (!isBarOwner) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="saved-bartenders-restricted-state">
        <Card className="bg-white/5 backdrop-blur-md border border-white/10">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-semibold text-white mb-2">Owner Access Only</h2>
            <p className="text-muted-foreground">Saved Bartenders is available for bar owner accounts.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="saved-bartenders-loading-state">
        <div className="h-40 rounded-xl border border-white/10 bg-white/5 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="saved-bartenders-page">
      <Card className="bg-white/5 backdrop-blur-md border border-white/10 mb-6">
        <CardHeader>
          <CardTitle className="text-3xl font-['Barlow_Condensed'] uppercase text-white flex items-center gap-2" data-testid="saved-bartenders-page-title">
            <Bookmark className="h-6 w-6 text-primary" />
            Saved Bartenders
          </CardTitle>
          <CardDescription data-testid="saved-bartenders-page-subtitle">
            Keep your shortlist ready and contact top candidates faster.
          </CardDescription>
        </CardHeader>
      </Card>

      {savedBartenders.length === 0 ? (
        <Card className="bg-white/5 backdrop-blur-md border border-white/10" data-testid="saved-bartenders-empty-state">
          <CardContent className="p-10 text-center">
            <h3 className="text-xl font-semibold text-white mb-2">No saved bartenders yet</h3>
            <p className="text-muted-foreground mb-4">Save bartenders while browsing to build your hiring shortlist.</p>
            <Button asChild data-testid="saved-bartenders-empty-browse-btn">
              <Link to="/owner/browse-bartenders">Browse Bartenders</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4" data-testid="saved-bartenders-list">
          {savedBartenders.map((bartender) => (
            <Card key={bartender.user_id} className="bg-white/5 backdrop-blur-md border border-white/10" data-testid={`saved-bartender-card-${bartender.user_id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Avatar className="h-14 w-14" data-testid={`saved-bartender-avatar-${bartender.user_id}`}>
                    <AvatarImage src={bartender.user?.profile_picture} alt={bartender.user?.first_name || 'Bartender'} />
                    <AvatarFallback>{bartender.user?.first_name?.[0] || 'B'}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold" data-testid={`saved-bartender-name-${bartender.user_id}`}>
                      {bartender.user?.first_name || 'Bartender'}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <span className="inline-flex items-center" data-testid={`saved-bartender-location-${bartender.user_id}`}>
                        <MapPin className="h-3 w-3 mr-1" />
                        {bartender.location?.city ? `${bartender.location.city}, ${bartender.location.state || ''}` : 'Location not specified'}
                      </span>
                      <span className="inline-flex items-center" data-testid={`saved-bartender-rating-${bartender.user_id}`}>
                        <Star className="h-3 w-3 mr-1 text-[#F5C15D] fill-current" />
                        {bartender.average_rating ? bartender.average_rating.toFixed(1) : 'N/A'} ({bartender.total_reviews || 0})
                      </span>
                    </div>
                    {bartender.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid={`saved-bartender-skills-${bartender.user_id}`}>
                        {bartender.skills.slice(0, 4).map((skill) => (
                          <Badge key={skill} variant="secondary" className="text-xs">{skill}</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/bartenders/${bartender.user_id}`)}
                      className="h-11"
                      data-testid={`saved-bartender-view-btn-${bartender.user_id}`}
                    >
                      <Eye className="h-4 w-4 mr-2" />View
                    </Button>
                    <Button
                      onClick={() => navigate(`/messages?user=${bartender.user_id}`)}
                      className="h-11"
                      data-testid={`saved-bartender-message-btn-${bartender.user_id}`}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />Message
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => unsaveBartender(bartender.user_id)}
                      className="h-11"
                      data-testid={`saved-bartender-remove-btn-${bartender.user_id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />Remove
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
